import type { SearchParameters } from 'data/conversational-search/types';
import type { ListingFilterBarType } from 'components/listing-filter-bar';

/**
 * Checks if a bedroom/bathroom/parking value is at default (meaning "any" or removed)
 */
const isDefaultRoomValue = (value: string | undefined | null): boolean => {
  return !value || value === '0+';
};

/**
 * Merges current AI search parameters with listing filter parameters
 * This ensures that the AI search parameters stay in sync with the current filter state
 * 
 * IMPORTANT: Only includes filters that are NOT at their default values.
 * This ensures removed filters (set to defaults) are not sent to the AI.
 * 
 * @param currentAiSearchParams - The existing AI search parameters
 * @param currentFilter - The current listing filter parameters
 * @returns Merged search parameters with only active (non-default) filters
 */
export const mergeSearchParamsWithFilter = (
  currentAiSearchParams: SearchParameters | null,
  currentFilter: ListingFilterBarType
): SearchParameters => {
  const aiSearchParams = currentAiSearchParams || {};
  const result: SearchParameters = { ...aiSearchParams };
  
  // Only include bedrooms/bathrooms/parking if they have a non-default value
  if (!isDefaultRoomValue(currentFilter.bedrooms)) {
    result.bedrooms = currentFilter.bedrooms;
  } else {
    delete result.bedrooms;
  }
  
  if (!isDefaultRoomValue(currentFilter.bathrooms)) {
    result.bathrooms = currentFilter.bathrooms;
  } else {
    delete result.bathrooms;
  }
  
  if (!isDefaultRoomValue(currentFilter.parkingSpaces)) {
    result.parkingSpaces = currentFilter.parkingSpaces;
  } else {
    delete result.parkingSpaces;
  }
  
  // Only include price if set
  if (currentFilter.priceMin) {
    result.priceMin = currentFilter.priceMin;
  } else {
    delete result.priceMin;
  }
  
  if (currentFilter.priceMax) {
    result.priceMax = currentFilter.priceMax;
  } else {
    delete result.priceMax;
  }
  
  // Only include sqft if set
  if (currentFilter.sqftMin) {
    result.sqftMin = currentFilter.sqftMin;
  } else {
    delete result.sqftMin;
  }
  
  if (currentFilter.sqftMax) {
    result.sqftMax = currentFilter.sqftMax;
  } else {
    delete result.sqftMax;
  }
  
  // Only include boolean amenities if true
  if (currentFilter.garage) {
    result.garage = true;
  } else {
    delete result.garage;
  }
  
  if (currentFilter.pool) {
    result.pool = true;
  } else {
    delete result.pool;
  }
  
  if (currentFilter.fireplace) {
    result.fireplace = true;
  } else {
    delete result.fireplace;
  }
  
  if (currentFilter.waterfront) {
    result.waterfront = true;
  } else {
    delete result.waterfront;
  }
  
  if (currentFilter.openHouse) {
    result.openHouse = true;
  } else {
    delete result.openHouse;
  }
  
  // Include rental if explicitly set (true = rental, false = for sale)
  if (currentFilter.rental !== undefined) {
    result.rental = currentFilter.rental;
  }
  
  // Include status if set
  if (currentFilter.status) {
    result.status = currentFilter.status;
  } else {
    delete result.status;
  }
  
  // Include date filters if set
  if (currentFilter.listedSince) {
    result.listedSince = currentFilter.listedSince;
  } else {
    delete result.listedSince;
  }
  
  if (currentFilter.listedTo) {
    result.listedTo = currentFilter.listedTo;
  } else {
    delete result.listedTo;
  }
  
  // Include hasImage if true
  if (currentFilter.hasImage) {
    result.hasImage = true;
  } else {
    delete result.hasImage;
  }
  
  // Include additional filters if they have meaningful values
  if (currentFilter.additional) {
    result.additional = currentFilter.additional;
  }
  
  // Include homeType if any property type is explicitly selected
  if (currentFilter.homeType) {
    result.homeType = currentFilter.homeType;
  }
  
  return result;
};
